import { useEffect, useState } from 'react';
import DataTable from '../../components/common/DataTable';
import DatePicker from '../../components/common/DatePicker';
import Select from '../../components/common/Select';
import { getWalletBalance, getWalletTransactions } from '../../services/walletService';
import { handleApiError } from '../../utils/handleApiError';
import api from '../../services/api';

const statusClass = (type) => {
  switch (type?.toUpperCase()) {
    case 'Debit': return 'bg-emerald-100 text-emerald-800';
    case 'Credit': return 'bg-amber-100 text-amber-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const StatusBadge = ({ type }) => (
  <span className={`text-[11px] font-medium tracking-wide uppercase px-2.5 py-0.5 rounded-full inline-block ${statusClass(type)}`}>
    {type}
  </span>
);

const columns = [
  {
    accessorKey: 'txn_id',
    header: 'Wallet Transaction ID',
    Cell: ({ cell, row }) => (
      <span className="font-mono text-xs text-gray-500">
        {cell.getValue() || row.original.wt_id || '—'}
      </span>
    ),
  },
  {
    accessorKey: 'created_at',
    header: 'Date',
    Cell: ({ cell }) => {
      const d = new Date(cell.getValue());
      return (
        <div>
          <div className="text-[13px] text-gray-900">{d.toLocaleDateString()}</div>
          <div className="text-[11px] text-gray-400 mt-0.5">{d.toLocaleTimeString()}</div>
        </div>
      );
    },
  },
  {
    accessorKey: 'approved_at',
    header: 'Approval Date',
    Cell: ({ cell }) => {
      const value = cell.getValue();
      if (!value) return <span className="text-[11px] text-gray-400">—</span>;
      const d = new Date(value);
      return (
        <div>
          <div className="text-[13px] text-gray-900">{d.toLocaleDateString()}</div>
          <div className="text-[11px] text-gray-400 mt-0.5">{d.toLocaleTimeString()}</div>
        </div>
      );
    },
  },
  {
    accessorKey: 'type',
    header: 'Type',
    Cell: ({ cell }) => {
      const type = cell.getValue();
      const t = type?.toLowerCase();
      return (
        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full uppercase ${t === 'debit' ? 'bg-red-50 text-red-600' :
            t === 'credit' ? 'bg-emerald-50 text-emerald-600' :
              t === 'hold' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-50 text-gray-600'
          }`}>
          {type || '—'}
        </span>
      );
    },
  },
  {
    accessorKey: 'description',
    header: 'Description',
    Cell: ({ cell }) => (
      <span className="text-[13px] font-medium text-gray-900">{cell.getValue() || '—'}</span>
    ),
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    Cell: ({ row }) => {
      const amt = parseFloat(row.original.amount);
      return (
        <div>
          <div className={`text-[13px] font-medium ${amt >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {amt >= 0 ? '+' : ''}₹{amt.toFixed(2)}
          </div>
          <div className="text-[11px] text-gray-400 mt-0.5">
            Before: ₹{parseFloat(row.original.balance_before || 0).toFixed(2)}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'balance_after',
    header: 'Balance Closing',
    Cell: ({ cell }) => (
      <span className="text-[13px] font-medium">₹{parseFloat(cell.getValue() || 0).toFixed(2)}</span>
    ),
  }
];

export default function WalletDashboard() {
  const LIMIT = 10;
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loadingBal, setLoadingBal] = useState(true);
  const [loadingTxn, setLoadingTxn] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFromDate, setAppliedFromDate] = useState('');
  const [appliedToDate, setAppliedToDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [appliedType, setAppliedType] = useState('');
  const [dateError, setDateError] = useState('');

  const [filterTrigger, setFilterTrigger] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const normalizePagination = (raw, fallbackPage, fallbackLimit, rowCount) => {
    const p = raw || {};
    const current = Number(
      p.current_page ?? p.page ?? p.currentPage ?? p.page_number
    ) || fallbackPage;
    const total = Number(p.total ?? p.total_count ?? p.count) || 0;
    const lastByTotal = total > 0 ? Math.ceil(total / fallbackLimit) : 0;
    const last = Number(
      p.last_page ?? p.total_pages ?? p.lastPage ?? p.page_count
    ) || lastByTotal || (rowCount === fallbackLimit ? current + 1 : current);
    return {
      current_page: current,
      last_page: Math.max(1, last),
      total,
      has_more: Boolean(p.has_more ?? current < Math.max(1, last)),
    };
  };

  const validateDates = (from, to) => {
    if (from && to && new Date(from) > new Date(to)) {
      setDateError('From date cannot be after To date.');
    } else {
      setDateError('');
    }
  };

  const applyFilter = () => {
    if (dateError) return;
    setPage(1);
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setAppliedType(typeFilter);
    setFilterTrigger(t => t + 1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (appliedFromDate)   params.append('from_date', appliedFromDate);
      if (appliedToDate)     params.append('to_date', appliedToDate);
      if (appliedType)       params.append('type', appliedType);
      if (searchText) params.append('search', searchText);

      const res = await api.get(`/wallet/export?${params.toString()}`, { responseType: 'blob' });

      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `wallet_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    getWalletBalance()
      .then((res) => setWalletData(res.data?.data || null))
      .catch(handleApiError)
      .finally(() => setLoadingBal(false));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearchQuery(searchText.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setLoadingTxn(true);
    getWalletTransactions({
      page,
      limit: LIMIT,
      fromDate: appliedFromDate,
      toDate: appliedToDate,
      type: appliedType,
      search: searchQuery,
    })
      .then((res) => {
        const d = res.data?.data;
        const rows = d?.transactions || [];
        const normalized = normalizePagination(
          d?.pagination || d?.meta || res.data?.pagination || res.data?.meta,
          page,
          LIMIT,
          rows.length
        );
        setTransactions(rows);
        setPagination(normalized);
        setPage(normalized.current_page);
      })
      .catch(handleApiError)
      .finally(() => setLoadingTxn(false));
  }, [page, filterTrigger, appliedFromDate, appliedToDate, appliedType, searchQuery]);

  const canGoNext = Boolean(
    pagination?.has_more ?? (transactions.length >= LIMIT)
  );
  const canShowPagination = Boolean(
    transactions.length > 0 && (page > 1 || canGoNext || (pagination?.last_page ?? 1) > 1)
  );
  const displayLastPage = Math.max(
    pagination?.last_page || 1,
    page + (canGoNext ? 1 : 0)
  );

  return (
    <div className="flex-1 overflow-y-auto p-8 bg-[#F8F9FA]">

      {/* Header Card */}
      <div className=" py-8 pt-0 flex items-center justify-between gap-6">

        {/* Left Content */}
        <div className="max-w-[512px]">
          <h2 className="text-[34px] font-bold text-[#2D0A3A] mb-2">
            Wallet Dashboard
          </h2>

          <p className="text-[15px] text-[#6B6471] leading-relaxed">
            Manage your liquid assets and view real-time transaction history. Your wallet is secured with enterprise-grade encryption.
          </p>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="flex gap-4 mb-6">
        <div className="bg-white rounded-[12px] shadow-[0px_20px_40px_0px_rgba(75,35,100,0.08)] flex items-center min-w-[250px] h-[100px]">
          <div className="w-[4px] h-full bg-[#754F89] rounded-l-[12px]" />
          <div className="px-5 w-full">
            <p className="text-[10px] font-semibold text-[#4C444E] tracking-[1.5px] uppercase mb-1">Available Balance</p>
            <p className="text-[28px] font-bold text-[#34094D]">{loadingBal ? '...' : `₹${parseFloat(walletData?.available_balance || 0).toFixed(2)}`}</p>
          </div>
        </div>
        <div className="bg-white rounded-[12px] shadow-[0px_20px_40px_0px_rgba(75,35,100,0.08)] flex items-center min-w-[250px] h-[100px]">
          <div className="w-[4px] h-full bg-[#4B2364] rounded-l-[12px]" />
          <div className="px-5 w-full">
            <p className="text-[10px] font-semibold text-[#4C444E] tracking-[1.5px] uppercase mb-1">Total Balance</p>
            <p className="text-[28px] font-bold text-[#34094D]">{loadingBal ? '...' : `₹${parseFloat(walletData?.total_balance || 0).toFixed(2)}`}</p>
          </div>
        </div>
        <div className="bg-white rounded-[12px] shadow-[0px_20px_40px_0px_rgba(75,35,100,0.08)] flex items-center min-w-[250px] h-[100px]">
          <div className="w-[4px] h-full bg-amber-400 rounded-l-[12px]" />
          <div className="px-5 w-full">
            <p className="text-[10px] font-semibold text-[#4C444E] tracking-[1.5px] uppercase mb-1">Hold Balance</p>
            <p className="text-[28px] font-bold text-yellow-500">{loadingBal ? '...' : `₹${parseFloat(walletData?.hold_balance || 0).toFixed(2)}`}</p>
          </div>
        </div>
      </div>

      <div className=" px-6 py-5 bg-[#F3F4F5] rounded-[16px] mb-3 border border-[#CEC3D01A]">
        <div className="flex items-center gap-3 flex-wrap justify-between">
          <div className="flex items-center gap-4 px-1 py-1">
            <div className="relative flex items-center gap-2">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><path d="M21 21l-4.35-4.35" strokeLinecap="round"></path></svg>
              <input
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="bg-[#FFFFFF] rounded-[12px] pl-8 pr-4 py-2.5 text-[13px] text-[#191C1D] border-none outline-none focus:ring-2 focus:ring-[#4B2364]/30 w-52 transition"
              />
            </div>
            <div className={`flex items-center gap-3 py-2 px-4 rounded-[12px] bg-white ring-1 transition-all ${dateError ? 'ring-red-400' : 'ring-transparent'}`}>
              <span className="text-[12px] font-semibold text-[#4C444E] uppercase">FROM</span>
              <DatePicker
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); validateDates(e.target.value, toDate); }}
                placeholder="mm/dd/yyyy"
                className="w-[110px]"
              />
            </div>
            <div className={`flex items-center gap-3 py-2 px-4 rounded-[12px] bg-white ring-1 transition-all ${dateError ? 'ring-red-400' : 'ring-transparent'}`}>
              <span className="text-[12px] font-semibold text-[#4C444E] uppercase">TO</span>
              <DatePicker
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); validateDates(fromDate, e.target.value); }}
                placeholder="mm/dd/yyyy"
                className="w-[110px]"
              />
            </div>
            <div className="flex items-center gap-3 py-2 px-4 rounded-[12px] bg-white min-w-[170px]">
              <span className="text-[12px] font-semibold text-[#4C444E] uppercase">TYPE</span>
              <Select
                value={typeFilter}
                onChange={(e) => {
                  const nextType = e.target.value;
                  setTypeFilter(nextType);
                  setAppliedType(nextType);
                  setPage(1);
                }}
                className="min-w-[110px]"
                options={[
                  { label: "All", value: "" },
                  { label: "Credit", value: "credit" },
                  { label: "Debit", value: "debit" },
                  { label: "Hold", value: "hold" },
                  { label: "Unhold", value: "unhold" },
                ]}
              />
            </div>
          </div>
          {dateError && <p className="text-[11px] text-red-500 font-medium">{dateError}</p>}
          <div className="flex items-center gap-2">
            <button
              onClick={applyFilter}
              disabled={Boolean(dateError)}
              className="flex items-center gap-2 text-[#FFFFFF] text-[14px] bg-[#34094D] font-medium hover:bg-[#34094D]/80 px-4 py-2 rounded-lg transition-colors disabled:opacity-60 cursor-pointer border-none"
            >
              Apply Filters
            </button>
            <button onClick={handleExport} disabled={exporting} className="flex items-center gap-2 text-[#FFFFFF] text-[14px] bg-[#34094D] font-medium hover:bg-[#34094D]/80 px-4 py-2 rounded-lg transition-colors disabled:opacity-60 cursor-pointer border-none">
              {exporting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>
      {/* Statement Card */}
      <div className="bg-[#FFFFFF] shadow-[0px_32px_48px_0px_#4B23640F] px-6 py-5 rounded-xl overflow-hidden">
        <div className='mb-5'>
          <p className="text-[20px] font-semibold text-[#34094D] mb-0.5">Wallet Statement</p>
          <p className="text-[14px] text-[#6B6471]">Real-time ledger of your financial activities</p>
        </div>

        {loadingTxn && transactions.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">Loading transactions...</div>
        ) : (
          <>
            {loadingTxn && transactions.length > 0 && (
              <div className="px-2 pb-3 text-xs text-gray-500">Refreshing data...</div>
            )}
            <DataTable columns={columns} data={transactions} />
            {transactions.length === 0 && (
              <div className="text-center py-10 text-gray-400 text-sm">No transactions found.</div>
            )}
          </>
        )}

        {/* Pagination */}
        {canShowPagination && (
          <div className="flex items-center justify-center gap-3 px-6 py-3.5 border-t border-gray-100">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className={`text-xs px-3.5 py-1.5 rounded-lg border border-gray-200 transition-colors ${page === 1 ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 cursor-pointer'}`}
            >
              ← Prev
            </button>
            <span className="text-xs text-gray-500">Page {page} of {displayLastPage}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!canGoNext}
              className={`text-xs px-3.5 py-1.5 rounded-lg border border-gray-200 transition-colors ${!canGoNext ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 cursor-pointer'}`}
            >
              Next →
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
